/* external */
import _ from "lodash";

/* internal */
import {
  DUE_AT,
  CREATED_AT,
  ASSIGNMENT,
  TITLE,
  PUBLISHED_AT,
} from "Gradebook/modules/Constants/stringConstants";
import { ASC, DESC, FINAL_GRADE, LOCAL_GRADE } from "Constants/stringConstants";

/* relative */
import {
  ASSESSMENT_NAME_A_Z,
  ASSESSMENT_NAME_Z_A,
  ASSESSMENTS_DETAILS,
  ASSIGNMENT_TYPE,
  CATEGORY_VIEW,
  CREATION_DATE,
  CREATION_DATE_NEWEST_TO_OLDEST,
  CREATION_DATE_OLDEST_TO_NEWEST,
  DUE_DATE,
  OPEN_DATE_EARLIEST_FIRST,
  OPEN_DATE_FARTHEST_FIRST,
  DUE_DATE_EARLIEST_FIRST,
  DUE_DATE_FARTHEST_FIRST,
  GRADING_CATEGORY,
  GRADING_PERIOD,
  GROUP_BY_CATEGORIES,
  OTHERS_DISPLAY_SETTINGS,
  CHECKLIST,
  RADIO_BUTTON,
  SHOW_CATEGORY_WITH_THE_ASSIGNMENT_NAME,
  SHOW_EXCLUDED_TASKS_FROM_FINAL_CALCULATION,
  SHOW_TASKS_FROM_OTHER_CLASSES,
  SORT_ASSESSMENTS_BY,
  ASSIGNMENT_DATE,
  EVALUATION_STATUS,
  HIGHLIGHT_CELLS,
  HIGHLIGHT_LATE_SUBMISSIONS,
  HIGHLIGHT_NOT_SUBMITTED,
  HIGHLIGHT_SUBMISSION_OVERDUE,
  HIGHLIGHT_SUBMISSION_PENDING,
  INITIAL_HIGHLIGHT_CELL_SETTINGS,
  OPEN_DATE,
} from "./DisplaySettingsConstants";

/**
 * Converts old array-based display settings to new object-based format
 * Provides backward compatibility with existing saved settings
 * @param {array|object} value - Old array format or new object format
 * @param {array} availableValues - Available values to initialize the object
 * @returns {object} Settings in object format with boolean flags
 */
const convertToObjectFormat = (value, availableValues = []) => {
  // If already in object format, return as is
  if (_.isObject(value) && !_.isArray(value)) {
    return value;
  }

  // If array format, convert to object
  if (_.isArray(value)) {
    const objectFormat = {};
    const valueSet = new Set(value);

    _.forEach(availableValues, item => {
      objectFormat[item] = valueSet.has(item);
    });
    return objectFormat;
  }

  // Fallback: return empty object
  return {};
};

const getVisibleItems = (items = []) =>
  _.reduce(
    items,
    (acc, item) => {
      const { isHidden = false, ...rest } = item;

      if (!isHidden) {
        acc.push(rest);
      }

      return acc;
    },
    []
  );

export const getDisplaySettingsItems = ({
  t,
  criteriaLabels,
  isCurriculumUBD = false,
  showSameSubjectGradeTasksInGradebook = false,
  hasCategories = false,
  isAllSubjectsSelected = false,
  isCourseBasedGrading = false,
  isUsingNewGradingSetupInCourses = false,
  isCumulativePeriod = false,
  isGradebookCompactUIEnabled = false,
  cumulativeTotalCalculationMethod = "",
}) => {
  const ASSIGNMENT_DATE_ITEMS = [
    {
      label: t("common:due_date"),
      value: DUE_DATE,
    },
    {
      label: t("common:creation_date"),
      value: CREATION_DATE,
    },
    {
      label: t("common:open_date"),
      value: OPEN_DATE,
    },
  ];

  const ASSESSMENT_DETAILS_ITEMS = [
    {
      label: `${t("common:assignment")} ${t("common:date_lowercase")}`,
      value: ASSIGNMENT_DATE,
      items: getVisibleItems(ASSIGNMENT_DATE_ITEMS),
      type: RADIO_BUTTON,
      isHidden: !isGradebookCompactUIEnabled,
    },
    {
      label: t("common:assignment_type"),
      value: ASSIGNMENT_TYPE,
    },
    {
      label: t("classRoom:grading_category"),
      value: GRADING_CATEGORY,
      isHidden: !hasCategories,
    },
    {
      label: t("common:due_date"),
      value: DUE_DATE,
      isHidden: isGradebookCompactUIEnabled,
    },
    {
      label: t("common:grading_period"),
      value: GRADING_PERIOD,
      isHidden:
        !isCumulativePeriod ||
        cumulativeTotalCalculationMethod !== "FROM_ASSIGNMENTS" ||
        !isUsingNewGradingSetupInCourses,
    },
    {
      label: t("common:creation_date"),
      value: CREATION_DATE,
      isHidden: true,
    },
    {
      label: t("common:open_date"),
      value: OPEN_DATE,
      isHidden: true,
    },
    {
      label: t("common:evaluation_status"),
      value: EVALUATION_STATUS,
      isHidden: !isGradebookCompactUIEnabled,
    },
  ];

  const SORT_ASSESSMENTS_BY_ITEMS = [
    {
      label: t("classRoom:assessment_name_a_z"),
      value: ASSESSMENT_NAME_A_Z,
    },
    {
      label: t("classRoom:assessment_name_z_a"),
      value: ASSESSMENT_NAME_Z_A,
    },
    {
      label: t("classRoom:open_date_earliest_first"),
      value: OPEN_DATE_EARLIEST_FIRST,
    },
    {
      label: t("classRoom:open_date_farthest_first"),
      value: OPEN_DATE_FARTHEST_FIRST,
    },
    {
      label: t("classRoom:due_date_earliest_first"),
      value: DUE_DATE_EARLIEST_FIRST,
    },
    {
      label: t("classRoom:due_date_farthest_first"),
      value: DUE_DATE_FARTHEST_FIRST,
    },
    {
      label: t("classRoom:creation_date_oldest_to_newest"),
      value: CREATION_DATE_OLDEST_TO_NEWEST,
    },
    {
      label: t("classRoom:creation_date_newest_to_oldest"),
      value: CREATION_DATE_NEWEST_TO_OLDEST,
    },
  ];

  const CATEGORY_VIEW_ITEMS = [
    {
      label: t("classRoom:group_by_categories"),
      value: GROUP_BY_CATEGORIES,
    },
    {
      label: t("classRoom:show_category_with_the_assignment_name"),
      value: SHOW_CATEGORY_WITH_THE_ASSIGNMENT_NAME,
    },
  ];

  const HIGHLIGHT_CELLS_ITEMS = [
    {
      label: t("classRoom:late_submission"),
      value: HIGHLIGHT_LATE_SUBMISSIONS,
      displayColor:
        INITIAL_HIGHLIGHT_CELL_SETTINGS[HIGHLIGHT_LATE_SUBMISSIONS]
          .displayColor,
      cellColor:
        INITIAL_HIGHLIGHT_CELL_SETTINGS[HIGHLIGHT_LATE_SUBMISSIONS].cellColor,
    },
    {
      label: t("classRoom:not_submitted"),
      value: HIGHLIGHT_NOT_SUBMITTED,
      displayColor:
        INITIAL_HIGHLIGHT_CELL_SETTINGS[HIGHLIGHT_NOT_SUBMITTED].displayColor,
      cellColor:
        INITIAL_HIGHLIGHT_CELL_SETTINGS[HIGHLIGHT_NOT_SUBMITTED].cellColor,
    },
    {
      label: t("classRoom:submission_overdue"),
      value: HIGHLIGHT_SUBMISSION_OVERDUE,
      displayColor:
        INITIAL_HIGHLIGHT_CELL_SETTINGS[HIGHLIGHT_SUBMISSION_OVERDUE]
          .displayColor,
      cellColor:
        INITIAL_HIGHLIGHT_CELL_SETTINGS[HIGHLIGHT_SUBMISSION_OVERDUE].cellColor,
    },
    {
      label: t("classRoom:submission_pending"),
      value: HIGHLIGHT_SUBMISSION_PENDING,
      displayColor:
        INITIAL_HIGHLIGHT_CELL_SETTINGS[HIGHLIGHT_SUBMISSION_PENDING]
          .displayColor,
      cellColor:
        INITIAL_HIGHLIGHT_CELL_SETTINGS[HIGHLIGHT_SUBMISSION_PENDING].cellColor,
    },
  ];

  /* other items */
  const finalGradeLabel = isCurriculumUBD
    ? _.get(criteriaLabels, LOCAL_GRADE, t("common:final_grade_text"))
    : _.get(criteriaLabels, FINAL_GRADE, t("common:final_grade_text"));

  const OTHERS_ITEMS = [
    {
      label: t("classRoom:show_tasks_excluded_from_final_calculation"),
      subLabel: t(
        "classRoom:enable_checkbox_to_show_task_from_gradebook_with_label",
        {
          finalGradeLabel,
        }
      ),
      value: SHOW_EXCLUDED_TASKS_FROM_FINAL_CALCULATION,
    },
    {
      label: t("classRoom:show_tasks_from_other_classes"),
      subLabel: t("classRoom:show_tasks_from_other_classes_desc"),
      value: SHOW_TASKS_FROM_OTHER_CLASSES,
      isHidden:
        !isUsingNewGradingSetupInCourses &&
        (!showSameSubjectGradeTasksInGradebook || isCourseBasedGrading),
    },
  ];

  const displaySettingsOptions = [
    {
      key: ASSESSMENTS_DETAILS,
      title: t("classRoom:assessments_details"),
      items: getVisibleItems(ASSESSMENT_DETAILS_ITEMS),
      type: CHECKLIST,
    },
    {
      key: HIGHLIGHT_CELLS,
      title: t("common:highlight_cells"),
      items: HIGHLIGHT_CELLS_ITEMS,
      type: CHECKLIST,
    },
    {
      key: SORT_ASSESSMENTS_BY,
      title: t("classRoom:sort_assessments_by"),
      items: SORT_ASSESSMENTS_BY_ITEMS,
      type: RADIO_BUTTON,
    },
    {
      key: CATEGORY_VIEW,
      title: t("classRoom:category_view"),
      items: CATEGORY_VIEW_ITEMS,
      type: RADIO_BUTTON,
      isHidden: !hasCategories || isAllSubjectsSelected || isCumulativePeriod,
    },
    {
      key: OTHERS_DISPLAY_SETTINGS,
      title: t("common:others"),
      items: getVisibleItems(OTHERS_ITEMS),
      type: CHECKLIST,
    },
  ];

  return getVisibleItems(displaySettingsOptions);
};

export const getAssignmentDetailsToShow = (displaySettings = {}) => {
  const selectedValues = _.get(displaySettings, ASSESSMENTS_DETAILS, {});

  const fieldsToCheck = {
    showAssignmentType: ASSIGNMENT_TYPE,
    showGradingCategory: GRADING_CATEGORY,
    showDueDate: DUE_DATE,
    showGradingPeriod: GRADING_PERIOD,
    showCreationDate: CREATION_DATE,
    showEvaluationStatus: EVALUATION_STATUS,
    showOpenDate: OPEN_DATE,
  };

  return _.mapValues(fieldsToCheck, value =>
    _.get(selectedValues, value, false)
  );
};

export const getAssignmentOrderBy = (displaySettings = {}) => {
  const selectedValuesObj = _.get(displaySettings, SORT_ASSESSMENTS_BY, {});
  // Get the first (and should be only) key that is true
  const selectedValue = _.findKey(selectedValuesObj, value => value === true);

  switch (selectedValue) {
    case ASSESSMENT_NAME_A_Z:
      return {
        entityType: ASSIGNMENT,
        orderBy: TITLE,
        orderByDirection: ASC,
      };

    case ASSESSMENT_NAME_Z_A:
      return {
        entityType: ASSIGNMENT,
        orderBy: TITLE,
        orderByDirection: DESC,
      };

    case OPEN_DATE_EARLIEST_FIRST:
      return {
        entityType: ASSIGNMENT,
        orderBy: PUBLISHED_AT,
        orderByDirection: ASC,
      };

    case OPEN_DATE_FARTHEST_FIRST:
      return {
        entityType: ASSIGNMENT,
        orderBy: PUBLISHED_AT,
        orderByDirection: DESC,
      };

    case DUE_DATE_EARLIEST_FIRST:
      return { entityType: ASSIGNMENT, orderBy: DUE_AT, orderByDirection: ASC };

    case DUE_DATE_FARTHEST_FIRST:
      return {
        entityType: ASSIGNMENT,
        orderBy: DUE_AT,
        orderByDirection: DESC,
      };

    case CREATION_DATE_OLDEST_TO_NEWEST:
      return {
        entityType: ASSIGNMENT,
        orderBy: CREATED_AT,
        orderByDirection: ASC,
      };

    case CREATION_DATE_NEWEST_TO_OLDEST:
    default:
      return {
        entityType: ASSIGNMENT,
        orderBy: CREATED_AT,
        orderByDirection: DESC,
      };
  }
};

export const getIsGroupByCategoryView = (displaySettings = {}) => {
  const selectedValuesObj = _.get(displaySettings, CATEGORY_VIEW, {});
  // Check if GROUP_BY_CATEGORIES is true
  return _.get(selectedValuesObj, GROUP_BY_CATEGORIES, false);
};

export const getOthersDisplaySettings = (displaySettings = {}) => {
  const selectedValues = _.get(displaySettings, OTHERS_DISPLAY_SETTINGS, {});

  const fieldsToCheck = {
    showFinalCalculationExcludedTask: SHOW_EXCLUDED_TASKS_FROM_FINAL_CALCULATION,
    showOtherClassesTasks: SHOW_TASKS_FROM_OTHER_CLASSES,
  };

  return _.mapValues(fieldsToCheck, value =>
    _.get(selectedValues, value, false)
  );
};

export const getIsDisplaySettingSelected = (
  selectedValues = {},
  key,
  value
) => {
  if (key === HIGHLIGHT_CELLS) {
    return _.get(selectedValues, [value, "enabled"], false);
  } else if (value === ASSIGNMENT_DATE) {
    return _.some([DUE_DATE, CREATION_DATE, OPEN_DATE], v =>
      _.get(selectedValues, v, false)
    );
  } else {
    return _.get(selectedValues, value, false);
  }
};

/**
 * Normalizes display settings to object format
 * Handles backward compatibility with old array-based format
 * @param {object} savedDisplaySettings - Display settings that may contain old array format
 * @returns {object} Normalized display settings in object format
 */
export const getNormalizedSavedDisplaySettings = (
  savedDisplaySettings = {}
) => {
  if (_.isEmpty(savedDisplaySettings)) {
    return savedDisplaySettings;
  }

  const normalizedSettings = { ...savedDisplaySettings };

  // Define available values for each setting key
  const availableValuesMap = {
    [ASSESSMENTS_DETAILS]: [
      ASSIGNMENT_TYPE,
      GRADING_CATEGORY,
      DUE_DATE,
      GRADING_PERIOD,
      CREATION_DATE,
      EVALUATION_STATUS,
      OPEN_DATE,
      ASSIGNMENT_DATE,
    ],
    [SORT_ASSESSMENTS_BY]: [
      ASSESSMENT_NAME_A_Z,
      ASSESSMENT_NAME_Z_A,
      DUE_DATE_EARLIEST_FIRST,
      DUE_DATE_FARTHEST_FIRST,
      CREATION_DATE_NEWEST_TO_OLDEST,
      CREATION_DATE_OLDEST_TO_NEWEST,
      OPEN_DATE_EARLIEST_FIRST,
      OPEN_DATE_FARTHEST_FIRST,
    ],
    [CATEGORY_VIEW]: [
      GROUP_BY_CATEGORIES,
      SHOW_CATEGORY_WITH_THE_ASSIGNMENT_NAME,
    ],
    [OTHERS_DISPLAY_SETTINGS]: [
      SHOW_EXCLUDED_TASKS_FROM_FINAL_CALCULATION,
      SHOW_TASKS_FROM_OTHER_CLASSES,
    ],
  };

  // Convert each setting if it's in old array format
  _.forEach(
    [
      ASSESSMENTS_DETAILS,
      SORT_ASSESSMENTS_BY,
      CATEGORY_VIEW,
      OTHERS_DISPLAY_SETTINGS,
    ],
    key => {
      const value = _.get(normalizedSettings, key);
      if (_.isArray(value)) {
        normalizedSettings[key] = convertToObjectFormat(
          value,
          availableValuesMap[key]
        );
      }
    }
  );

  return normalizedSettings;
};
